import {
  ranks,
  suits,
  type Card,
  type Rank,
  type Suit,
} from "./types";

export const RANK_LABELS: Record<Rank, string> = {
  "3": "3",
  "4": "4",
  "5": "5",
  "6": "6",
  "7": "7",
  "8": "8",
  "9": "9",
  T: "10",
  J: "Jack",
  Q: "Queen",
  K: "King",
  A: "Ace",
  "2": "2",
};

export const SUIT_SYMBOLS: Record<Suit, string> = {
  d: "♦",
  h: "♥",
  s: "♠",
  c: "♣",
};

export type PlayKind =
  | "single"
  | "pair"
  | "triple"
  | "straight"
  | "flush"
  | "full-house"
  | "four-of-a-kind"
  | "straight-flush";

export interface EvaluatedPlay {
  kind: PlayKind;
  cards: Card[];
  category: number;
  primary: number;
  kickers: number[];
  suitTie: number;
}

export interface PlayValidation {
  valid: boolean;
  message: string;
  evaluation?: EvaluatedPlay;
}

const rankIndex = new Map<Rank, number>(
  ranks.map((rank, index) => [rank, index]),
);
const suitIndex = new Map<Suit, number>(
  suits.map((suit, index) => [suit, index]),
);

export function createDeck(): Card[] {
  return ranks.flatMap((rank) =>
    suits.map((suit) => `${rank}${suit}` as Card),
  );
}

export function cardRank(card: Card): number {
  return rankIndex.get(card[0] as Rank) ?? -1;
}

export function cardSuit(card: Card): number {
  return suitIndex.get(card[1] as Suit) ?? -1;
}

export function compareCards(first: Card, second: Card): number {
  return cardRank(first) - cardRank(second) || cardSuit(first) - cardSuit(second);
}

export function sortCards(cards: readonly Card[]): Card[] {
  return [...cards].sort(compareCards);
}

function descending(values: readonly number[]): number[] {
  return [...values].sort((a, b) => b - a);
}

function straightDetails(rankValues: number[]): {
  valid: boolean;
  high: number;
  highRank: number;
} {
  const unique = [...new Set(rankValues)].sort((a, b) => a - b);

  if (unique.length !== 5) {
    return { valid: false, high: -1, highRank: -1 };
  }

  const isConsecutive = unique.every(
    (value, index) => index === 0 || value === unique[index - 1]! + 1,
  );

  if (isConsecutive) {
    const high = unique.at(-1)!;
    return { valid: true, high, highRank: high };
  }

  // The source game explicitly allowed 2-3-4-5-6 as its one low straight.
  const isTwoLow = unique.join(",") === "0,1,2,3,12";
  return isTwoLow
    ? { valid: true, high: 3, highRank: 3 }
    : { valid: false, high: -1, highRank: -1 };
}

function highestSuitForRank(cards: readonly Card[], rank: number): number {
  return Math.max(
    ...cards
      .filter((card) => cardRank(card) === rank)
      .map((card) => cardSuit(card)),
  );
}

export function evaluatePlay(input: readonly Card[]): EvaluatedPlay | null {
  const cards = sortCards(input);
  const uniqueCards = new Set(cards);

  if (cards.length === 0 || uniqueCards.size !== cards.length) {
    return null;
  }

  const rankValues = cards.map(cardRank);
  const counts = new Map<number, number>();
  for (const rank of rankValues) {
    counts.set(rank, (counts.get(rank) ?? 0) + 1);
  }

  if (cards.length === 1) {
    return {
      kind: "single",
      cards,
      category: 0,
      primary: rankValues[0]!,
      kickers: [],
      suitTie: cardSuit(cards[0]!),
    };
  }

  if (cards.length === 2 && counts.size === 1) {
    return {
      kind: "pair",
      cards,
      category: 0,
      primary: rankValues[0]!,
      kickers: [],
      suitTie: Math.max(...cards.map(cardSuit)),
    };
  }

  if (cards.length === 3 && counts.size === 1) {
    return {
      kind: "triple",
      cards,
      category: 0,
      primary: rankValues[0]!,
      kickers: [],
      suitTie: Math.max(...cards.map(cardSuit)),
    };
  }

  if (cards.length !== 5) {
    return null;
  }

  const flush = cards.every((card) => card[1] === cards[0]![1]);
  const straight = straightDetails(rankValues);
  const countEntries = [...counts.entries()];
  const fourRank = countEntries.find(([, count]) => count === 4)?.[0];
  const tripleRank = countEntries.find(([, count]) => count === 3)?.[0];
  const pairRank = countEntries.find(([, count]) => count === 2)?.[0];

  if (straight.valid && flush) {
    return {
      kind: "straight-flush",
      cards,
      category: 4,
      primary: straight.high,
      kickers: [],
      suitTie: highestSuitForRank(cards, straight.highRank),
    };
  }

  if (fourRank !== undefined) {
    const kicker = rankValues.find((rank) => rank !== fourRank)!;
    return {
      kind: "four-of-a-kind",
      cards,
      category: 3,
      primary: fourRank,
      kickers: [kicker],
      suitTie: highestSuitForRank(cards, fourRank),
    };
  }

  if (tripleRank !== undefined && pairRank !== undefined) {
    return {
      kind: "full-house",
      cards,
      category: 2,
      primary: tripleRank,
      kickers: [pairRank],
      suitTie: highestSuitForRank(cards, tripleRank),
    };
  }

  if (flush) {
    const rankedCards = [...cards].sort((a, b) => compareCards(b, a));
    return {
      kind: "flush",
      cards,
      category: 1,
      primary: cardRank(rankedCards[0]!),
      kickers: rankedCards.map(cardRank),
      suitTie: cardSuit(rankedCards[0]!),
    };
  }

  if (straight.valid) {
    return {
      kind: "straight",
      cards,
      category: 0,
      primary: straight.high,
      kickers: [],
      suitTie: highestSuitForRank(cards, straight.highRank),
    };
  }

  return null;
}

function compareNumberArrays(first: readonly number[], second: readonly number[]) {
  const length = Math.max(first.length, second.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (first[index] ?? -1) - (second[index] ?? -1);
    if (difference !== 0) return difference;
  }
  return 0;
}

export function compareEvaluatedPlays(
  challenger: EvaluatedPlay,
  table: EvaluatedPlay,
): number {
  if (challenger.cards.length !== table.cards.length) {
    return -1;
  }

  return (
    challenger.category - table.category ||
    challenger.primary - table.primary ||
    compareNumberArrays(challenger.kickers, table.kickers) ||
    challenger.suitTie - table.suitTie
  );
}

export function validatePlay(
  cards: readonly Card[],
  tableCards: readonly Card[] | null = null,
): PlayValidation {
  const evaluation = evaluatePlay(cards);
  if (!evaluation) {
    return {
      valid: false,
      message:
        "Choose a single, pair, triple, or a valid five-card combination.",
    };
  }

  if (!tableCards || tableCards.length === 0) {
    return { valid: true, message: describePlay(evaluation), evaluation };
  }

  const table = evaluatePlay(tableCards);
  if (!table) {
    return { valid: false, message: "The current table play is invalid." };
  }

  if (cards.length !== tableCards.length) {
    return {
      valid: false,
      message: `Play exactly ${tableCards.length} card${
        tableCards.length === 1 ? "" : "s"
      } to match the table.`,
    };
  }

  if (compareEvaluatedPlays(evaluation, table) <= 0) {
    return {
      valid: false,
      message: `Your ${describePlay(evaluation).toLowerCase()} does not beat ${describePlay(
        table,
      ).toLowerCase()}.`,
    };
  }

  return { valid: true, message: describePlay(evaluation), evaluation };
}

function pluralRank(rank: Rank): string {
  const label = RANK_LABELS[rank];
  if (rank === "6") return "Sixes";
  if (rank === "T") return "Tens";
  return `${label}s`;
}

export function cardLabel(card: Card): string {
  const rank = card[0] as Rank;
  const suit = card[1] as Suit;
  return `${RANK_LABELS[rank]}${SUIT_SYMBOLS[suit]}`;
}

export function describePlay(play: EvaluatedPlay): string {
  const rank = ranks[play.primary]!;
  switch (play.kind) {
    case "single":
      return `Single ${cardLabel(play.cards[0]!)}`;
    case "pair":
      return `Pair of ${pluralRank(rank)}`;
    case "triple":
      return `Triple ${pluralRank(rank)}`;
    case "straight":
      return `Straight to ${RANK_LABELS[rank]}`;
    case "flush":
      return `Flush, ${RANK_LABELS[rank]} high`;
    case "full-house":
      return `Full house, ${pluralRank(rank)} over`;
    case "four-of-a-kind":
      return `Four ${pluralRank(rank)}`;
    case "straight-flush":
      return `Straight flush to ${RANK_LABELS[rank]}`;
  }
}

export function secureShuffle(cards: readonly Card[]): Card[] {
  const shuffled = [...cards];
  for (let remaining = shuffled.length; remaining > 1; remaining -= 1) {
    const limit = Math.floor(0x1_0000_0000 / remaining) * remaining;
    let value = 0;
    do {
      const random = new Uint32Array(1);
      crypto.getRandomValues(random);
      value = random[0]!;
    } while (value >= limit);

    const index = value % remaining;
    const last = remaining - 1;
    [shuffled[index], shuffled[last]] = [shuffled[last]!, shuffled[index]!];
  }
  return shuffled;
}

export function deterministicShuffle(
  cards: readonly Card[],
  values: readonly number[],
): Card[] {
  const shuffled = [...cards];
  let cursor = 0;
  for (let remaining = shuffled.length; remaining > 1; remaining -= 1) {
    const raw = values[cursor % values.length] ?? 0;
    cursor += 1;
    const index = Math.abs(raw) % remaining;
    const last = remaining - 1;
    [shuffled[index], shuffled[last]] = [shuffled[last]!, shuffled[index]!];
  }
  return shuffled;
}
